// Dependency posture: this package names no vendor, performs no I/O, and holds no behavior beyond zod, since a schema
// here is a type declaration that happens to execute. It is also a leaf — it imports nothing from @storyboard/,
// because @storyboard/db's generated Drizzle schema imports facts types from here to type its `facts` columns, and any
// Storyboard dependency would close a cycle that `tsc --build` refuses. `test/unit/packageBoundary.test.ts` pins both.
//
// `z` comes from @hono/zod-openapi rather than `zod` because only that copy carries the `.openapi()` the spec
// generator reads, and only schemas built with it reach the web client's generated types. Same import as `api-types`.
import { z } from "@hono/zod-openapi";

/**
 * The streams the backend collates events into.
 *
 * Collation vocabulary only: a stream is a route parameter and a browser collection, decided by the registry
 * (`definitionsIn(stream)`), never a field on an event or a column on a row. Events ship as separate homogeneous
 * streams, so a document needs no discriminator — and keeping it off the entity means a subject can be reassigned to
 * another stream by editing one registry line, with no data migration.
 *
 * Not every stream leaves the backend; {@link TMS_EVENT_STREAM_EXPOSURE} says which do, and the wire is typed by
 * {@link PublishedTmsEventStreamSchema} rather than by this. `assignment` is the first internal one: the feed
 * interleaves `route_event` and `driver_event`, while a server-side read folds `assignment` into who-was-on-which-unit
 * intervals and no client ever sees a transition on its own.
 */
export const TmsEventStreamSchema = z.enum(["assignment", "driver_event", "route_event"]);

export type TmsEventStream = z.infer<typeof TmsEventStreamSchema>;

/**
 * Whether a stream leaves the backend.
 *
 * A `published` stream is what the stream endpoint serves and what the post-write ping names. An `internal` stream is
 * collated and stored like any other and reaches no client: its events exist for a server-side read to roll up, and
 * nothing outside the backend ever sees one raw. Total over the vocabulary, so adding a stream is this decision first.
 */
export type TmsEventStreamExposure = "internal" | "published";

export const TMS_EVENT_STREAM_EXPOSURE = {
  [TmsEventStreamSchema.enum.assignment]: "internal",
  [TmsEventStreamSchema.enum.driver_event]: "published",
  [TmsEventStreamSchema.enum.route_event]: "published",
} as const satisfies Record<TmsEventStream, TmsEventStreamExposure>;

/**
 * The streams that leave the backend: the vocabulary narrowed to what {@link TMS_EVENT_STREAM_EXPOSURE} publishes.
 */
export type PublishedTmsEventStream = {
  [S in TmsEventStream]: (typeof TMS_EVENT_STREAM_EXPOSURE)[S] extends "published" ? S : never;
}[TmsEventStream];

/**
 * Whether a stream leaves the backend, as a narrowing.
 */
export function isPublishedTmsEventStream(stream: TmsEventStream): stream is PublishedTmsEventStream {
  return TMS_EVENT_STREAM_EXPOSURE[stream] === "published";
}

export const PUBLISHED_TMS_EVENT_STREAMS: readonly PublishedTmsEventStream[] =
  TmsEventStreamSchema.options.filter(isPublishedTmsEventStream);

/**
 * The stream vocabulary as the wire knows it: the published streams alone, under the name the spec has always used.
 *
 * Every wire schema that names a stream — the page, the ping — is built on this, so an internal stream cannot reach a
 * generated client by any route: not as a value, and not as a type.
 */
export const PublishedTmsEventStreamSchema = z
  .enum(PUBLISHED_TMS_EVENT_STREAMS as [PublishedTmsEventStream, ...PublishedTmsEventStream[]])
  .openapi("TmsEventStream");

/**
 * The one catalog key that is not a category: the home of kinds the vocabulary declines to classify.
 *
 * A key rather than a `null` key, so the map stays a plain nested object and the directory mirroring it has a name.
 * Its kinds are the only ones whose schemas carry `category: null`, which is what a client narrows on.
 */
export const UNCATEGORIZED_CATEGORY = "uncategorized";

export type UncategorizedCategory = typeof UNCATEGORIZED_CATEGORY;

/**
 * What every TMS event carries regardless of kind: identity, time, tenancy.
 *
 * Nothing else. Not the stream, which is the registry's; not the taxonomy, which the facts carry as their own triple;
 * and no driver or vehicle reference, because which of those an event has is a property of its subject, not of events.
 */
export const tmsEventBase = z.object({
  /** The anchor id — the event's identity everywhere, including its row's primary key. */
  id: z.string(),
  /** The kernel `Subject` value; also the name of both of the subject's tables. */
  subject: z.string(),
  occurredAt: z.iso.datetime(),
  workspaceId: z.string(),
  workspaceIntegrationId: z.string(),
  /**
   * When the event's row was written: its arrival, which a late vendor makes later than `occurredAt`.
   *
   * Stamped by the reader from the row and served on every stream event; never supplied by a writer, and absent on a
   * DTO that has not been stored yet, which is why it is optional here.
   */
  createdAt: z.iso.datetime().optional(),
});

/**
 * The triple that identifies one schema.
 *
 * `category` is the map key the kind sits under, and `version` its revision; together they index the catalog.
 */
export type TmsEventSchemaId<
  TCategory extends string = string,
  TKind extends string = string,
  TVersion extends number = number,
> = {
  readonly category: TCategory;
  readonly kind: TKind;
  readonly version: TVersion;
};

/**
 * The triple as it appears on a parsed value rather than on an id.
 *
 * Identical but for `category`, which is `null` on the reserved key's kinds — the one place the stored document and
 * the map key it was declared under deliberately disagree.
 */
type TmsEventFactsTriple = { readonly category: string | null; readonly kind: string; readonly version: number };

/**
 * A facts type minus its identifying triple: the fields a resolved domain record carries for that kind.
 */
export type TmsEventFactFields<TFacts extends TmsEventFactsTriple> = Omit<TFacts, keyof TmsEventSchemaId>;

/**
 * The `category` a kind's schemas state: the literal key it sits under, or `null` for the reserved one.
 */
type CategoryField<TCategory extends string> = TCategory extends UncategorizedCategory
  ? z.ZodNull
  : z.ZodLiteral<TCategory>;

/**
 * The identifying triple as zod fields.
 *
 * Carried inside `facts` as well as on the row's own columns, which is what lets a stored value name its own schema.
 */
type TaxonomyShape<TCategory extends string, TKind extends string, TVersion extends number> = {
  category: CategoryField<TCategory>;
  kind: z.ZodLiteral<TKind>;
  version: z.ZodLiteral<TVersion>;
};

/**
 * One kind at one version: its identifying triple, its facts schema, and its full DTO schema.
 *
 * `facts` is the triple as literals plus the kind's own fields, strict — the shape of the `facts` jsonb and the thing
 * `parseTmsEventFacts` resolves by lookup. `schema` is the wire DTO: base, refs, and facts flattened together,
 * strict, OpenAPI-named `TmsEvent<Kind>V<n>`. Both are built by {@link tmsEventKind}, the only way either is ever
 * constructed.
 */
export type TmsEventKindVersion<
  TCategory extends string = string,
  TKind extends string = string,
  TVersion extends number = number,
  TRefs extends z.ZodRawShape = z.ZodRawShape,
  TFacts extends z.ZodRawShape = z.ZodRawShape,
> = {
  readonly id: TmsEventSchemaId<TCategory, TKind, TVersion>;
  readonly facts: z.ZodObject<TaxonomyShape<TCategory, TKind, TVersion> & TFacts, z.core.$strict>;
  readonly schema: z.ZodObject<
    typeof tmsEventBase.shape & TRefs & TaxonomyShape<TCategory, TKind, TVersion> & TFacts,
    z.core.$strict
  >;
};

/**
 * Declares one kind at one version from its triple, its subject's reference shape, and its own fields.
 *
 * Refs and facts are passed separately because they land in different places on the row — refs as columns, facts as
 * the jsonb — and this is the one place that knows to put the triple inside the facts. Strict everywhere, so a field
 * the catalog does not name is rejected at the boundary: the allowlist, in code.
 *
 * @param id - Written here and again as the kind's placement in the catalog; the map's type refuses any disagreement.
 * @param shape - `refs` is the subject's reference mixin composition, shared by every kind the subject states;
 *   `facts` is this kind's own fields and nothing it shares with its siblings.
 */
export function tmsEventKind<
  TCategory extends string,
  TKind extends string,
  TVersion extends number,
  TRefs extends z.ZodRawShape,
  TFacts extends z.ZodRawShape,
>(
  id: TmsEventSchemaId<TCategory, TKind, TVersion>,
  shape: { refs: TRefs; facts: TFacts },
): TmsEventKindVersion<TCategory, TKind, TVersion, TRefs, TFacts> {
  assertFactsAreDisjoint(id, shape);

  const taxonomy = {
    category: id.category === UNCATEGORIZED_CATEGORY ? z.null() : z.literal(id.category),
    kind: z.literal(id.kind),
    version: z.literal(id.version),
  } as TaxonomyShape<TCategory, TKind, TVersion>;

  // The two casts are the seam between a spread, which TypeScript types positionally, and the declared intersection.
  // They are sound because the spreads are these exact objects, and they are confined to the sole construction point.
  const facts = z.strictObject({ ...taxonomy, ...shape.facts }) as TmsEventKindVersion<
    TCategory,
    TKind,
    TVersion,
    TRefs,
    TFacts
  >["facts"];

  const schema = z
    .strictObject({ ...tmsEventBase.shape, ...shape.refs, ...taxonomy, ...shape.facts })
    .openapi(openApiNameFor(id)) as TmsEventKindVersion<TCategory, TKind, TVersion, TRefs, TFacts>["schema"];

  return { id, facts, schema };
}

/**
 * Refuses a kind whose facts would shadow a base field, a reference, or part of the identifying triple.
 *
 * The DTO is one flat object built by spreading base, references, the triple, then facts, so a facts field sharing a
 * name with any earlier layer silently replaces it rather than failing. A kind declaring `occurredAt` would end up
 * with the base's instant gone and no sign of it; one declaring `vendorVehicleKey` would take its subject's
 * reference out of the row, where the registry's reference check would then see the column with no field.
 *
 * @param id - The kind-version being declared, named in the error so the offending file is obvious.
 * @param shape - Its references and facts, whose key sets must not meet.
 * @throws if a facts key collides with the base, the triple, or one of the kind's references.
 */
function assertFactsAreDisjoint(id: TmsEventSchemaId, shape: { refs: z.ZodRawShape; facts: z.ZodRawShape }): void {
  const reserved = new Set([
    ...Object.keys(tmsEventBase.shape),
    ...Object.keys(shape.refs),
    "category",
    "kind",
    "version",
  ]);
  const collisions = Object.keys(shape.facts).filter((field) => reserved.has(field));

  if (collisions.length > 0) {
    throw new Error(
      `TMS event kind "${id.kind}" version ${id.version} declares facts that shadow its own DTO: ${collisions.join(", ")}`,
    );
  }
}

/**
 * The component name the DTO registers under, e.g. `TmsEventFuelStopV1`.
 *
 * What the generated web client names each arm of the union it narrows on `kind`.
 */
function openApiNameFor(id: TmsEventSchemaId): string {
  return `TmsEvent${id.kind.charAt(0).toUpperCase()}${id.kind.slice(1)}V${id.version}`;
}

/**
 * The catalog's type: a kind-version may only sit at the path its own `id` names.
 *
 * Each leaf must be a {@link TmsEventKindVersion} whose `id` literals are the very category, kind, and version it is
 * keyed under; one declared `{ category: "call", … }` and placed under `fuel` resolves to `never`, and the
 * `satisfies` on the map fails to compile at that leaf. This is what makes a miscategorized kind inexpressible rather
 * than merely tested against: the triple is written once in the kind file and once as its placement, and the type
 * system refuses any disagreement between them.
 */
export type TmsEventCatalog<TCatalog> = {
  readonly [TCategory in keyof TCatalog & string]: {
    readonly [TKind in keyof TCatalog[TCategory] & string]: {
      readonly [TVersion in keyof TCatalog[TCategory][TKind] &
        number]: TCatalog[TCategory][TKind][TVersion] extends TmsEventKindVersion<TCategory, TKind, TVersion>
        ? TCatalog[TCategory][TKind][TVersion]
        : never;
    };
  };
};

/**
 * Declares the catalog, holding every leaf to the placement its own triple names.
 *
 * The self-referential constraint is what checks the map against itself: `TCatalog` is inferred from the argument and
 * then required to satisfy {@link TmsEventCatalog} over that same inference, so a kind-version sitting at the wrong
 * key resolves to `never` and the error lands on that leaf rather than on the map. Written as a function rather than
 * as `satisfies TmsEventCatalog<typeof tmsEventCatalog>`, which TypeScript rejects outright (TS7022) for referencing
 * the declaration inside its own initializer. `const` keeps the version keys literal, so the returned type indexes.
 */
export function tmsEventCatalogOf<const TCatalog extends TmsEventCatalog<TCatalog>>(catalog: TCatalog): TCatalog {
  return catalog;
}
